import { cx } from "@/lib/tailwind-utils";

import { ItemType } from "@/types/enums/item-type";
import { ShopCategory, ShopItem } from "@/types/shop";
import type { ShopDisplayStyle } from "@/types/tenant";

export const SHOP_OTHER_CATEGORY = "Other";

export function shouldGroupShopItems(itemCount: number): boolean {
  return itemCount > 5;
}

const SHOP_COLUMN_GRID_CLASS_NAME =
  "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3";

export function getShopCatalogItemsLayoutClasses(itemCount: number): string {
  if (shouldGroupShopItems(itemCount)) {
    return SHOP_COLUMN_GRID_CLASS_NAME;
  }
  // Direct children include both public item wrappers and preview SortableItems.
  // Growing gap-adjusted bases cap each row at 1/2/3 items while filling short rows.
  return "flex flex-wrap gap-4 [&>*]:min-w-0 [&>*]:grow [&>*]:basis-full sm:[&>*]:basis-[calc(50%-0.5rem)] lg:[&>*]:basis-[calc((100%-2rem)/3)]";
}

export const SHOP_STOREFRONT_GRID_CLASS_NAME =
  "grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-10";

/** Storefront shops use more of the page so product photos read larger. */
export function getShopCatalogWidthClass(
  displayStyle: ShopDisplayStyle | undefined,
): string {
  return displayStyle === "STOREFRONT" ? "max-w-6xl" : "max-w-5xl";
}

/** Storefront products and medications open their own page instead of a popup. */
export function opensShopProductPage(
  item: Pick<ShopItem, "type">,
  displayStyle: ShopDisplayStyle | undefined,
): boolean {
  return (
    displayStyle === "STOREFRONT" &&
    (item.type === ItemType.PRODUCT || item.type === ItemType.MEDICATION)
  );
}

/** A section made only of storefront product tiles gets the denser retail grid. */
export function getShopGroupItemsLayoutClasses(
  items: Pick<ShopItem, "type">[],
  catalogItemCount: number,
  displayStyle: ShopDisplayStyle | undefined,
): string {
  if (
    items.length > 0 &&
    items.every((item) => opensShopProductPage(item, displayStyle))
  ) {
    return SHOP_STOREFRONT_GRID_CLASS_NAME;
  }
  // Gift cards keep a card shape, so a lone one stretched across a short row grows as tall as it is wide.
  if (items.some((item) => item.type === ItemType.GIFT_CARD)) {
    return SHOP_COLUMN_GRID_CLASS_NAME;
  }
  const flexLayout = getShopCatalogItemsLayoutClasses(catalogItemCount);
  // In the stretchy flex layout, a lone Storefront tile would grow into a huge square photo.
  return displayStyle === "STOREFRONT"
    ? cx(flexLayout, "[&>*:has([data-storefront-tile])]:max-w-xs")
    : flexLayout;
}

const itemTypeLabels: Partial<Record<ItemType, string>> = {
  [ItemType.SERVICE]: "service",
  [ItemType.PRODUCT]: "product",
  [ItemType.MEDICATION]: "medication",
  [ItemType.PACKAGE]: "package",
  [ItemType.GIFT_CARD]: "gift card",
};

export function buildShopTopLevelCategoryLookup(
  categories: ShopCategory[],
): Map<string, string> {
  const categoryByName = new Map(
    categories
      .filter((category) => !category.isOther)
      .map((category) => [category.name, category]),
  );
  const roots = new Map<string, string>();

  const resolveRoot = (name: string, seen = new Set<string>()): string => {
    const cached = roots.get(name);
    if (cached) return cached;
    const category = categoryByName.get(name);
    const parent = category?.parentCategoryName?.trim();
    if (!parent || !categoryByName.has(parent) || seen.has(name)) {
      roots.set(name, name);
      return name;
    }
    seen.add(name);
    const root = resolveRoot(parent, seen);
    roots.set(name, root);
    return root;
  };

  for (const category of categories) {
    if (!category.isOther) resolveRoot(category.name);
  }
  return roots;
}

export function getShopFilterCategories(
  items: ShopItem[],
  categories: ShopCategory[],
): { value: string; label: string }[] {
  const roots = buildShopTopLevelCategoryLookup(categories);
  const available = new Set(
    items.map((item) => {
      const category = item.category?.trim();
      return category ? (roots.get(category) ?? category) : SHOP_OTHER_CATEGORY;
    }),
  );
  const options: { value: string; label: string; order: number }[] = [];
  const seen = new Set<string>();
  for (const category of [...categories].sort((a, b) => a.order - b.order)) {
    const value = category.isOther ? SHOP_OTHER_CATEGORY : category.name;
    if (
      (roots.get(value) ?? value) !== value ||
      seen.has(value) ||
      !available.has(value)
    )
      continue;
    seen.add(value);
    options.push({ value, label: category.name, order: category.order });
  }
  for (const value of Array.from(available)) {
    if (!seen.has(value))
      options.push({ value, label: value, order: Infinity });
  }
  return options
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    .map(({ value, label }) => ({ value, label }));
}

export const SHOP_SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "name", label: "Name: A to Z" },
] as const;

export type ShopSort = (typeof SHOP_SORTS)[number]["value"];

export const DEFAULT_SHOP_SORT: ShopSort = "featured";

export function parseShopSort(value: string | null | undefined): ShopSort {
  return SHOP_SORTS.find((sort) => sort.value === value)?.value ?? "featured";
}

const shopSortPrice = (item: ShopItem) => item.displayPrice ?? item.price;

/**
 * "Featured" keeps the server order: popular items first, then the staff-set
 * rank. Every other sort is stable, so ties keep that featured order.
 */
export function sortShopItems(items: ShopItem[], sort: ShopSort): ShopItem[] {
  if (sort === "featured") return items;
  const featuredIndex = new Map(items.map((item, index) => [item.id, index]));
  const compare = (a: ShopItem, b: ShopItem): number => {
    switch (sort) {
      case "price-asc":
        return shopSortPrice(a) - shopSortPrice(b);
      case "price-desc":
        return shopSortPrice(b) - shopSortPrice(a);
      case "name":
        return a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        });
      default:
        return 0;
    }
  };
  return [...items].sort(
    (a, b) =>
      compare(a, b) ||
      (featuredIndex.get(a.id) ?? 0) - (featuredIndex.get(b.id) ?? 0),
  );
}

/** The top-level category an item is filed under in the shop's category filter. */
export function getShopItemFilterCategory(
  item: Pick<ShopItem, "category">,
  categories: ShopCategory[],
): string {
  const category = item.category?.trim();
  if (!category) return SHOP_OTHER_CATEGORY;
  return buildShopTopLevelCategoryLookup(categories).get(category) ?? category;
}

/** Uncategorized items get one shop section per item type, e.g. "Other:PRODUCT". */
export function shopOtherSectionId(type: ItemType): string {
  return `${SHOP_OTHER_CATEGORY}:${type}`;
}

/** The shop section an item is listed under, as tagged with `data-shop-category`. */
export function getShopItemSectionId(
  item: Pick<ShopItem, "category" | "type">,
  categories: ShopCategory[],
): string {
  const category = getShopItemFilterCategory(item, categories);
  return category === SHOP_OTHER_CATEGORY
    ? shopOtherSectionId(item.type)
    : category;
}

/** Other visible products and medications from the same top-level category. */
export function getRelatedShopItems(
  item: ShopItem,
  items: ShopItem[],
  categories: ShopCategory[],
  limit = 4,
): ShopItem[] {
  const category = getShopItemFilterCategory(item, categories);
  return items
    .filter(
      (candidate) =>
        candidate.id !== item.id &&
        (candidate.type === ItemType.PRODUCT ||
          candidate.type === ItemType.MEDICATION) &&
        getShopItemFilterCategory(candidate, categories) === category,
    )
    .slice(0, limit);
}

export function filterShopItems(
  items: ShopItem[],
  options: {
    searchQuery: string;
    selectedCategory?: string;
    categories: ShopCategory[];
  },
): ShopItem[] {
  const { searchQuery, selectedCategory, categories } = options;
  const roots = buildShopTopLevelCategoryLookup(categories);
  const categoryDescriptions = new Map(
    categories.map((category) => [category.name, category.description ?? ""]),
  );
  const terms = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);

  return items.filter((item) => {
    const category = item.category?.trim() || SHOP_OTHER_CATEGORY;
    const rootCategory =
      category === SHOP_OTHER_CATEGORY
        ? SHOP_OTHER_CATEGORY
        : (roots.get(category) ?? category);
    if (selectedCategory && rootCategory !== selectedCategory) return false;

    if (terms.length === 0) return true;
    const promotions = [
      item.bestPromotion,
      ...(item.applicablePromotions ?? []),
      ...(item.availablePromotions ?? []),
    ];
    const searchable = [
      item.name,
      item.description,
      category,
      categoryDescriptions.get(category),
      categoryDescriptions.get(rootCategory),
      itemTypeLabels[item.type],
      ...promotions.flatMap((promotion) =>
        promotion ? [promotion.name, promotion.description] : [],
      ),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
}
